/**
 * FreeUsageExhaustedGate — opens `FreeUsageExhaustedModal` whenever any
 * request is refused with HTTP 402 (the backend's `require_free_usage`).
 *
 * The HTTP client announces the 402 on `window` (`FREE_USAGE_EXHAUSTED_EVENT`),
 * so every gated flow — training, generation, playground, deploy, download —
 * shares one modal without wiring each call site. While the modal is open,
 * further 402s (e.g. a retry or a parallel request) are ignored instead of
 * stacking. The gate renders nothing unless billing is enabled.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { GlobalContext } from '@/app/providers/globalState';
import { useAuth } from '@/features/auth/AuthContext';
import { BILLING_TAB, settingsTabPath } from '@/features/settings/components/settingsTabs.js';
import { FREE_USAGE_EXHAUSTED_EVENT } from '@/shared/http/client';

import FreeUsageExhaustedModal from './FreeUsageExhaustedModal';

export function FreeUsageExhaustedGate() {
    const navigate = useNavigate();
    const { currentUsecaseId } = useContext(GlobalContext) || {};
    const { billingEnabled, isAuthEnabled, role } = useAuth();
    const [open, setOpen] = useState(false);

    const enabled = billingEnabled === true;
    const canUpgrade = !isAuthEnabled || role === 'Admin';

    useEffect(() => {
        if (!enabled) return undefined;
        const show = () => setOpen(true);
        window.addEventListener(FREE_USAGE_EXHAUSTED_EVENT, show);
        return () => window.removeEventListener(FREE_USAGE_EXHAUSTED_EVENT, show);
    }, [enabled]);

    const close = useCallback(() => setOpen(false), []);
    const upgrade = useCallback(() => {
        setOpen(false);
        navigate(settingsTabPath(BILLING_TAB.id, currentUsecaseId));
    }, [navigate, currentUsecaseId]);

    if (!enabled) return null;
    return <FreeUsageExhaustedModal open={open} canUpgrade={canUpgrade} onClose={close} onUpgrade={upgrade} />;
}

export default FreeUsageExhaustedGate;
